import React from "react";
import * as Mui from "@mui/material";
import * as XLSX from "xlsx";
import { CalendarMonth, Category, DateRange, Description, Payments, Person, Source, TaskAlt } from "@mui/icons-material";
import dayjs from "dayjs";
import { designColors, designRadii, designType, designWeights } from "services/constants/designTokens";
import { tooltipStyleProps } from "services/constants/Styles";

// Shared bits for Payroll > Pay Inputs > Reimbursements (same look as the
// Bonus pay input).

const TONES = {
  green: { bg: "#E7F8EF", fg: "#067647" },
  blue: { bg: "#EAF2FF", fg: "#1D4ED8" },
  amber: { bg: "#FFF4E5", fg: "#B54708" },
  red: { bg: "#FEF2F2", fg: "#B42318" },
  slate: { bg: designColors.slate100, fg: designColors.slate600 },
};

// Inline status chip.
export const Chip = ({ label, tone = "slate", sx }) => {
  const colors = TONES[tone] || TONES.slate;
  return (
    <Mui.Box
      component="span"
      sx={{
        ...designType.captionText,
        fontWeight: designWeights.semibold,
        display: "inline-block",
        px: 1,
        py: 0.25,
        borderRadius: designRadii.compactButton,
        backgroundColor: colors.bg,
        color: colors.fg,
        whiteSpace: "nowrap",
        lineHeight: 1.5,
        verticalAlign: "middle",
        ...sx,
      }}
    >
      {label}
    </Mui.Box>
  );
};

// ₹ amounts: whole rupees without decimals, others with 2.
export const money = (value) => {
  if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) return "—";
  const number = Number(value);
  return number.toLocaleString("en-IN", Number.isInteger(number) ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};


export const downloadSheet = (fileName, sheetName, rows) => {
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(rows.length ? rows : [{}]), sheetName);
  XLSX.writeFile(book, fileName);
};

// Screen status codes from the API.
export const CLAIM_STATUS = {
  PENDING: { label: "Pending", tone: "amber" },
  READY: { label: "Ready", tone: "green" },
  PROCESSED: { label: "Processed", tone: "blue" },
  REJECTED: { label: "Rejected", tone: "red" },
  CANCELLED: { label: "Cancelled", tone: "slate" },
};

// Status badge. The stored claim status (e.g. Returned, Paid) shows on
// hover when it differs from the screen status.
export const StatusChip = ({ row }) => {
  const meta = CLAIM_STATUS[row.statusCode] || { label: row.statusLabel || "—", tone: "slate" };
  const label = row.statusLabel || meta.label;
  const chip = <Chip label={label} tone={meta.tone} />;
  return row.claimStatus && row.claimStatus !== label ? (
    <Mui.Tooltip title={`Claim status: ${row.claimStatus}`} arrow slotProps={tooltipStyleProps}>
      <Mui.Box component="span">{chip}</Mui.Box>
    </Mui.Tooltip>
  ) : chip;
};

export const formatDate = (value) => (value ? dayjs(value).format("DD MMM YYYY") : "—");

// Amount shown = the approver's reduced amount when there is one.
export const shownAmount = (row) => (row.amountApproved != null ? row.amountApproved : row.amount);

// Pending claims show the cycle they are expected in, marked with *.
export const cycleText = (cycle) => (cycle ? `${cycle.name}${cycle.expected ? " *" : ""}` : "—");

// CustomFilter headers = the table columns (mockup order).
export const claimHeaders = [
  { name: "Bill Date", key: "bill_date_label", type: "string", icon: <CalendarMonth fontSize="small" /> },
  { name: "Employee", key: "employee_label", type: "autocomplete", icon: <Person fontSize="small" /> },
  { name: "Type", key: "typeName", type: "autocomplete", icon: <Category fontSize="small" /> },
  { name: "Description", key: "description", type: "string", icon: <Description fontSize="small" /> },
  { name: "Amount (₹)", key: "shown_amount", type: "number", icon: <Payments fontSize="small" />, showBetweenOperator: false },
  { name: "Source", key: "sourceLabel", type: "autocomplete", icon: <Source fontSize="small" /> },
  { name: "Status", key: "statusLabel", type: "autocomplete", icon: <TaskAlt fontSize="small" /> },
  { name: "Payroll Cycle", key: "payroll_cycle_name", type: "autocomplete", icon: <DateRange fontSize="small" /> },
];

